import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, Facebook, Tiktok, Instagram, Linkedin, Twitter } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input.tsx";
import { Textarea } from "../components/ui/textarea.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.tsx";
import { toast } from "../hooks/use-toast";
import Swal from 'sweetalert2';

const Contact = () => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // --- سيتم التعديل داخل هذه الدالة ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const reviewData = { name, phone, email, message };
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_PROJECTS_API}components/submit_message.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData),
      });
      const result = await response.json();

      if (response.ok) {
        // --- تعديل 1: استخدام الترجمة لرسالة النجاح ---
        Swal.fire({
          title: t('contact.swal.successTitle'),
          text: t('contact.swal.successText'),
          icon: 'success',
          background: '#f0f8ff',
          color: '#705540',
          iconColor: '#705540',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      } else {
        // --- تعديل 2: استخدام الترجمة لرسالة الخطأ ---
        Swal.fire({
          title: t('contact.swal.errorTitle'),
          text: result.message, // رسالة الخطأ من السيرفر لا تحتاج ترجمة
          icon: 'error',
          background: '#f0f8ff',
          color: '#FE6464',
          iconColor: '#FE6464',
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      // --- تعديل 3: استخدام الترجمة لرسالة خطأ الشبكة ---
      Swal.fire({
        title: t('contact.swal.errorTitle'),
        text: t('contact.swal.networkErrorText'),
        icon: 'error',
        background: '#f0f8ff',
        color: '#FE6464',
        iconColor: '#FE6464',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }

    setName('');
    setPhone('');
    setEmail('');
    setMessage('');
  };

  const contactDetails = [
    {
      titleKey: "companyProfile.alexOfficeAddress", // For Alexandria Head Office
      phone1Key: "companyProfile.alexMobile1",
      phone2Key: "companyProfile.alexMobile2",
      landlineKey: "companyProfile.alexLandline",
      icon: MapPin
    },
    {
      titleKey: "companyProfile.beauBoisShowroomAddress",
      phone1Key: "companyProfile.beauBoisMobile",
      landlineKey: "companyProfile.beauBoisLandline",
      icon: MapPin
    },
    {
      titleKey: "companyProfile.cairoShowroomAddress", // For Cairo Showroom
      icon: MapPin
      // Add phone numbers if available for Cairo or if they are general
    }
  ];


  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/HusseinDecorativeFurniture?mibextid=ZbWKwL", color: "hover:text-blue-600" },
    { icon: Instagram, href: "https://www.instagram.com/husseinabdelfatahfurniture?igsh=OGFpNmZmN2F6bmhn", color: "hover:text-pink-600" },
  ];

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t('nav.contact')} {/* "Contact Us" */}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('contact.subtitle')} {/* Original subtitle for form encouragement */}
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >

              <div className="space-y-6">
                {contactDetails.map((detail, index) => (
                  <Card key={index} className="shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-2">
                        <div className={`w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-lg shrink-0`}>
                          <detail.icon className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-800 text-lg">{t(detail.titleKey)}</h4>
                      </div>
                      {detail.phone1Key && <p className="text-gray-600 ml-14">{t(detail.phone1Key)}</p>}
                      {detail.phone2Key && <p className="text-gray-600 ml-14">{t(detail.phone2Key)}</p>}
                      {detail.landlineKey && <p className="text-gray-600 ml-14">{t(detail.landlineKey)}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4 mt-10">
                  {t('contact.followUs')}
                </h4>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 transition-colors duration-300 ${social.color}`}
                    >
                      <social.icon className="w-5 h-5" />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card className="border-2 border-amber-200 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-800">
                    {t('contact.sendMessage')} {/* Original key for form title */}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.name')}
                      </label>
                      <Input
                        id="contact-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('contact.name')}
                        required
                        className="w-full h-12 border-amber-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.email')}
                      </label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('contact.email')}
                        required
                        className="w-full h-12 border-amber-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.phone')}
                      </label>
                      <Input
                        id="contact-phone"
                        type="number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={t('contact.phone')}
                        required
                        className="w-full h-12 border-amber-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.message')}
                      </label>
                      <Textarea
                        id="contact-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('contact.message')}
                        required
                        rows={5}
                        className="w-full border-amber-200 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t('contact.send')}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;